import * as React from 'react';
import {
  Button,
  MenuItem,
  MenuList,
  Popover,
  useTheme,
  Tooltip,
} from '@material-ui/core';
import { Avatar, Body, Heading, Icon } from 'copilot-design-system';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { Company } from 'src/constants';
import { useLogout } from 'src/hooks/useLogout';
import { FlagsContext, PortalConfigContext } from 'src/context';
import { setActiveCompanyId } from 'src/store/activeCompany/slice';
import { useGetClientCompaniesQuery } from 'src/services/api/companiesApi';
import { useCompanySwitcherVisibility } from 'src/features/companies/useCompanySwitcherVisibility';
import history from 'src/history';
import { applicationSettingsApi } from 'src/services/api/applicationsApi';
import { ApiTags } from 'src/services/api/appApi';
import { shallowEqual } from 'react-redux';
import { contractsApi } from 'src/services/api/contractsApi';
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'src/store/reduxTypes';
import { isUnreadNotification } from 'src/utils/NotificationsUtils';

const selectCompanyUnreadNotifications = createSelector(
  [
    (state: RootState) => state.notifications.fileNotifications,
    (state: RootState) => state.notifications.newInvoiceNotifications,
    (state: RootState) => state.notifications.formRequestNotifications,
    (state: RootState) => state.notifications.esigRequestNotifications,
  ],

  (
    fileNotifications,
    newInvoiceNotifications,
    formRequestNotifications,
    esigRequestNotifications,
  ) => {
    const companyToUnreadCount = new Map<string, number>();

    // Combine all notification types
    const allNotifications = [
      ...fileNotifications,
      ...newInvoiceNotifications,
      ...formRequestNotifications,
      ...esigRequestNotifications,
    ];

    // Filter only unread notifications
    const unreadNotifications = allNotifications.filter(isUnreadNotification);

    // Group unread notifications by companyId
    for (const notification of unreadNotifications) {
      const { companyId } = notification;
      if (!companyId) continue; // Skip if companyId is missing
      companyToUnreadCount.set(
        companyId,
        (companyToUnreadCount.get(companyId) || 0) + 1,
      );
    }

    return companyToUnreadCount;
  },
);

const useCompanySwitcher = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { SignoutRedirectURL: signoutRedirectURL } =
    React.useContext(PortalConfigContext);
  const companySwitcherVisibility = useCompanySwitcherVisibility();
  const { EnableManyCompanies } = React.useContext(FlagsContext);

  const { activeClientCompanyId, isClient } = useAppSelector(
    (state) => ({
      activeClientCompanyId: state.activeCompany.activeCompanyId,
      isClient: state.user.isClient,
    }),
    shallowEqual,
  );
  const { data: companies = [], isLoading } = useGetClientCompaniesQuery();

  const companyToUnreadNotifications = useAppSelector(
    selectCompanyUnreadNotifications,
  );

  const companyToUnreadMessages = useAppSelector(
    (state) => state.messages.totalUnreadMessagesByCompany,
  );

  const getCompanyUnreadNotificationsCount = React.useCallback(
    (companyId: string) => {
      const unreadAppsNotifications =
        companyToUnreadNotifications.get(companyId) ?? 0;
      const unreadMessages = companyToUnreadMessages?.[companyId] ?? 0;

      return unreadAppsNotifications + unreadMessages;
    },
    [companyToUnreadNotifications, companyToUnreadMessages],
  );

  const handleLogout = useLogout();
  const handleClick = (company: Company) => () => {
    dispatch(setActiveCompanyId(company.id));
    history.push('/');
  };

  React.useEffect(() => {
    // if there are companies and no active company, set the first company as active
    // TODO: this is a temporary solution, we will discuss what should be the default company
    // either the last active company or the first company in the list.
    if (companies && companies.length > 0 && !activeClientCompanyId) {
      const localActiveCompanyId = localStorage.getItem('activeCompanyId');
      const activeCompanyId =
        localActiveCompanyId ?? companies.at(0)?.id ?? null;
      dispatch(setActiveCompanyId(activeCompanyId));
    }
  }, [companies, activeClientCompanyId]);

  React.useEffect(() => {
    if (!isClient) {
      return;
    }
    if (EnableManyCompanies) {
      localStorage.setItem('activeCompanyId', activeClientCompanyId ?? '');
    } else {
      localStorage.removeItem('activeCompanyId');
    }
  }, [activeClientCompanyId, EnableManyCompanies, isClient]);

  React.useEffect(() => {
    dispatch(applicationSettingsApi.util.invalidateTags([ApiTags.apps]));
  }, [dispatch, activeClientCompanyId]);

  const selectedCompany = React.useMemo(() => {
    return companies?.find((company) => company.id === activeClientCompanyId);
  }, [companies, activeClientCompanyId]);

  React.useEffect(() => {
    dispatch(contractsApi.util.invalidateTags([ApiTags.contracts]));
  }, [dispatch, activeClientCompanyId]);

  return {
    companies,
    companySwitcherVisibility,
    handleClick,
    handleLogout,
    isLoading,
    theme,
    selectedCompany,
    signoutRedirectURL,
    getCompanyUnreadNotificationsCount,
  } as const;
};

export const DesktopCompanySwitcher = ({
  sidebarForegroundColor,
}: {
  sidebarForegroundColor: string;
}) => {
  const {
    companies,
    companySwitcherVisibility,
    handleClick,
    selectedCompany,
    getCompanyUnreadNotificationsCount,
  } = useCompanySwitcher();
  if (companySwitcherVisibility.visibility !== 'desktop') {
    return null;
  }

  return (
    <div className="flex flex-col items-center gap-3 py-5">
      {companies.map((company) => (
        <Tooltip
          title={company.fields.name}
          key={company.id}
          placement="right"
          enterDelay={1000}
        >
          <button
            className="bg-transparent border-none shadow-none cursor-pointer relative"
            onClick={handleClick(company)}
          >
            <Avatar
              alt={`${company.fields.name} logo`}
              style={{
                borderWidth: selectedCompany?.id === company.id ? 1 : 0,
                borderColor:
                  selectedCompany?.id === company.id
                    ? sidebarForegroundColor
                    : 'transparent',
              }}
              fallbackColor={company.fields.fallbackColor}
              size="md"
              src={company.fields.avatarImageURL || ''}
              text={company.fields.name.charAt(0).toUpperCase()}
              variant="rounded"
            />

            {getCompanyUnreadNotificationsCount(company.id) > 0 ? (
              <div className="absolute top-[-5px] right-[-5px]">
                <Heading
                  className="px-[4.5px] rounded bg-secondary-hover"
                  size="xs"
                  tag="span"
                >
                  {getCompanyUnreadNotificationsCount(company.id)}
                </Heading>
              </div>
            ) : null}
          </button>
        </Tooltip>
      ))}
    </div>
  );
};

export const MobileCompanySwitcher = () => {
  const {
    companies,
    companySwitcherVisibility,
    handleClick,
    handleLogout,
    theme,
    selectedCompany,
    signoutRedirectURL,
    getCompanyUnreadNotificationsCount,
  } = useCompanySwitcher();
  const [open, setOpen] = React.useState(false);
  const anchorEl = React.useRef<HTMLButtonElement>(null);

  if (companySwitcherVisibility.visibility !== 'mobile') {
    return null;
  }

  return (
    <div>
      <Button
        className="bg-transparent border-none shadow-none w-full flex items-center justify-start px-2"
        ref={anchorEl}
        onClick={() => setOpen(true)}
        style={{
          // TODO: remove this once we integrate the theme in tailwind
          backgroundColor: theme.palette.primary.dark,
        }}
      >
        {selectedCompany && (
          <>
            <Avatar
              className="mr-2 block sm:hidden"
              src={selectedCompany?.fields.avatarImageURL || ''}
              size="sm"
              fallbackColor={selectedCompany?.fields.fallbackColor}
              alt={selectedCompany?.fields.name || ''}
              variant="rounded"
              text={selectedCompany.fields.name.slice(0, 1)}
            />
            <Body className="text-white truncate" size="sm">
              {selectedCompany?.fields.name}
            </Body>
            <Icon
              icon={open ? 'ChevronUp' : 'ChevronDown'}
              className={'w-[12px] h-[12px] ml-[6px] text-white'}
            />
          </>
        )}
      </Button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        classes={{
          paper: 'w-[240px] mt-[2px] ml-[-8px] shadow-dropdown-menu',
        }}
      >
        <MenuList className="p-0">
          {companies.map((company) => (
            <MenuItem
              key={company.id}
              onClick={handleClick(company)}
              className="h-8 p-3 flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <Avatar
                  src={company.fields.avatarImageURL || ''}
                  size="sm"
                  fallbackColor={company.fields.fallbackColor}
                  alt={company.fields.name}
                  variant="rounded"
                  text={company.fields.name.slice(0, 1)}
                />
                <Heading className="text-[14px]">{company.fields.name}</Heading>
              </div>
              {getCompanyUnreadNotificationsCount(company.id) > 0 ? (
                <Heading
                  className="px-[4.5px] rounded bg-secondary-hover"
                  size="xs"
                  tag="span"
                >
                  {getCompanyUnreadNotificationsCount(company.id)}
                </Heading>
              ) : null}
            </MenuItem>
          ))}
          <div className="h-[1px] bg-secondary-hover" />
          <MenuItem
            className="h-8 p-3"
            onClick={() => handleLogout(false, signoutRedirectURL, true)}
          >
            <Body className="text-[14px]">Log out</Body>
          </MenuItem>
        </MenuList>
      </Popover>
    </div>
  );
};
